import { Menu as BaseMenu } from "@base-ui/react/menu";
import { Check } from "lucide-react";
import { cn } from "./cn";

const popupClasses =
    "min-w-40 border border-border bg-surface p-1 shadow-lg outline-none " +
    "transition-all origin-[var(--transform-origin)] " +
    // <sm: iOS-style action sheet
    "max-sm:w-full max-sm:max-h-[70dvh] max-sm:overflow-auto max-sm:rounded-t-[14px] " +
    "max-sm:border-x-0 max-sm:border-b-0 max-sm:p-2 " +
    "max-sm:pb-[max(0.5rem,env(safe-area-inset-bottom))] " +
    "max-sm:duration-300 max-sm:ease-out " +
    "max-sm:data-[starting-style]:translate-y-full max-sm:data-[ending-style]:translate-y-full " +
    // ≥sm: anchored dropdown
    "sm:rounded-md sm:duration-100 sm:max-h-[calc(var(--available-height)-0.5rem)] sm:overflow-y-auto " +
    "sm:data-[starting-style]:scale-95 sm:data-[starting-style]:opacity-0 " +
    "sm:data-[ending-style]:scale-95 sm:data-[ending-style]:opacity-0";

const itemClasses =
    "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none " +
    "max-sm:py-2.5 max-sm:text-[15px] " +
    "data-[highlighted]:bg-accent data-[highlighted]:text-accent-fg " +
    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed";

/**
 * Dropdown menu (Base UI). Compose as:
 *
 *   <Menu.Root>
 *     <Menu.Trigger render={<Button>Actions</Button>} />
 *     <Menu.Content>
 *       <Menu.Item onClick={…}>Edit</Menu.Item>
 *       <Menu.Separator />
 *       <Menu.Item onClick={…}>Delete</Menu.Item>
 *       <Menu.CheckboxItem checked={on} onCheckedChange={setOn}>Pinned</Menu.CheckboxItem>
 *     </Menu.Content>
 *   </Menu.Root>
 */
export const Menu = {
    Root: BaseMenu.Root,
    Trigger: BaseMenu.Trigger,
    Group: BaseMenu.Group,
    GroupLabel: function GroupLabel({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseMenu.GroupLabel>) {
        return (
            <BaseMenu.GroupLabel
                className={cn("px-2 py-1 text-xs font-medium text-muted", className as string)}
                {...props}
            />
        );
    },
    Item: function Item({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseMenu.Item>) {
        return <BaseMenu.Item className={cn(itemClasses, className as string)} {...props} />;
    },
    /** Toggle row with a tick on the right; stays open on click, for picking several. */
    CheckboxItem: function CheckboxItem({
        className,
        children,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseMenu.CheckboxItem>) {
        return (
            <BaseMenu.CheckboxItem className={cn(itemClasses, className as string)} {...props}>
                {children}
                <BaseMenu.CheckboxItemIndicator keepMounted className="ml-auto flex size-4 items-center justify-center data-[unchecked]:invisible">
                    <Check className="size-3.5" />
                </BaseMenu.CheckboxItemIndicator>
            </BaseMenu.CheckboxItem>
        );
    },
    Separator: function Separator({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseMenu.Separator>) {
        return (
            <BaseMenu.Separator
                className={cn("-mx-1 my-1 h-px bg-border", className as string)}
                {...props}
            />
        );
    },
    /** Portal + positioner + popup in one convenient wrapper. */
    Content: function Content({
        className,
        children,
        sideOffset = 4,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseMenu.Positioner>) {
        return (
            <BaseMenu.Portal>
                {/* z-index on the Positioner (the positioned element), not the Popup.
                    On phones the !important utilities beat Base UI's inline anchoring styles
                    so the menu docks to the bottom edge as an action sheet. */}
                <BaseMenu.Positioner
                    className={
                        "z-[1100] max-sm:fixed! max-sm:inset-x-0! max-sm:top-auto! " +
                        "max-sm:bottom-0! max-sm:transform-none!"
                    }
                    sideOffset={sideOffset}
                    {...props}
                >
                    <BaseMenu.Popup className={cn(popupClasses, className as string)}>
                        {children}
                    </BaseMenu.Popup>
                </BaseMenu.Positioner>
            </BaseMenu.Portal>
        );
    },
};
